import { useEffect, useState } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useSearchParameters } from "@/components/signup/hooks";
import { ProductKey } from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import { useBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src/index";
import { DataFoundationsAttributes } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/types.ts";
import { DataFoundationsWrapper } from "@/utils/data-foundations/data-foundation-types";

import { useConfirmationPage } from "../../hooks/use-confirmation-page";
import { useScreenManager } from "../../hooks/use-screen-manager";
import { getPrimaryProduct } from "../../hooks/utils/use-cloud-status-utils";
import {
    USER_SEGMENTATION_TEAM_TYPES,
    USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE,
    UserSegmentationTeamTypes
} from "../../models/user-segmentation";
import { getCurrentUrlWithParams } from "../../utils/utils";

import {
    ATLAS_TICKET,
    TRACKING_EVENT_ACTION_SUBJECT_IDS,
    USER_SEARCH_PARAMS
} from "./constants";
import TeamFocusView from "./team-focus-view";

const ViewManager = () => {
    const stargateClient = useBxpWebStargateClient();
    const { nextScreen } = useScreenManager();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const {
        userSegmentationContext: {
            closeUserSegmentationUI,
            setSelectedTeamType
        }
    } = useConfirmationPage();

    const searchParameters = new URLSearchParams(window.location.search);
    const paramsTeamType = (searchParameters.get(USER_SEARCH_PARAMS.teamType) ??
        "") as UserSegmentationTeamTypes;

    const { products, edition } = useSearchParameters();
    const productKey = getPrimaryProduct(products);
    const { profile } = useUserContext();
    const userType = profile.cohorts?.userType;
    const domainType = profile.cohorts?.domainType;
    const companyType = profile.cohorts?.companyType;
    const [waitForAttributes, setWaitForAttributes] = useState(true);
    const [dataFoundationAttributes, setDataFoundationAttributes] =
        useState<DataFoundationsAttributes>({
            productKey,
            edition
        });

    useEffect(() => {
        if (!profile.requestInProgress) {
            setDataFoundationAttributes((prevAttributes) => ({
                ...prevAttributes,
                userType: userType,
                domainType: domainType,
                companyType: companyType
            }));
            setWaitForAttributes(false);
        }
    }, [profile.requestInProgress, userType, domainType, companyType]);

    useEffect(() => {
        // Suppress purple user segmentation view during N2E Flow.
        stargateClient.postUserSegmentationViewAttempt();
    }, []);

    useEffect(() => {
        // If team type is set as a query parameter, the user has refreshed the page.
        // Add the selected team type to state and close the user segmentation UI.
        if (USER_SEGMENTATION_TEAM_TYPES.includes(paramsTeamType)) {
            setSelectedTeamType(paramsTeamType);

            const isServiceCollectionFlow = products.includes(
                ProductKey.SERVICE_COLLECTION
            );
            const isCustomerServiceSelected =
                paramsTeamType === USER_SEGMENTATION_TEAM_TYPE_CUSTOMER_SERVICE;

            if (isServiceCollectionFlow && isCustomerServiceSelected) {
                const currentUrl = new URL(window.location.href);
                if (!currentUrl.searchParams.get("primaryCollectionProduct")) {
                    const updatedUrl = getCurrentUrlWithParams({
                        primaryCollectionProduct:
                            ProductKey.JIRA_CUSTOMER_SERVICE
                    });
                    history.replaceState({}, "", updatedUrl);
                }
            }

            closeUserSegmentationUI();
            nextScreen();
            return;
        }

        createAnalyticsEvent({
            action: "viewed",
            eventType: "screen",
            name: TRACKING_EVENT_ACTION_SUBJECT_IDS.userSegRedesignScreen,
            attributes: {
                atlasTicket: ATLAS_TICKET
            }
        }).fire();
    }, [paramsTeamType]);

    return USER_SEGMENTATION_TEAM_TYPES.includes(paramsTeamType) ? null : (
        <DataFoundationsWrapper
            dataFoundationsEventOption="TeamChoiceScreenViewed"
            attributes={dataFoundationAttributes}
            waitForAttributes={waitForAttributes}
        >
            <TeamFocusView />
        </DataFoundationsWrapper>
    );
};

export default ViewManager;
